/**
 * The check-in / check-out form contract (ADR 0009) — HR-configured forms, the tasks an employee
 * proposes at check-in, the manager's per-task decision, and the end-of-day report at check-out.
 *
 * TWO FORMS, ONE SHAPE. HR designs a `check_in` form and a `check_out` form independently, each with a
 * `mode` (`off` | `optional` | `required`) and an ordered list of fields. Title and Project on the check-in
 * form are fixed — a task cannot exist without them — and are NOT part of `fields`; everything else is
 * HR's. Four presets (`priority`, `due_date`, `estimate_hours`, `description`) carry a `system` marker so
 * their answer lands in the real task column; every other answer is stored on the task as a custom field.
 *
 * EVERY SUBMITTED TASK IS ITS OWN APPROVAL. The manager approves or rejects each task on its own (with an
 * "approve all" convenience); approving creates a normal project task for the employee, rejecting stores the
 * reason and tells the employee, who can edit and resubmit. Clock-in never waits for that approval —
 * a `required` check-in form is satisfied by SUBMITTING at least one task.
 *
 * THE CHECK-OUT FORM IS A REPORT, NOT AN APPROVAL. Per task the employee touched: the status afterwards,
 * actual time, a note — plus HR's day-level fields. Submitting updates those tasks' statuses; it does not
 * create time entries (logging time stays the timesheet's job).
 *
 * Follows leave.contract.ts's conventions: DTOs distinct from the Prisma models, `paginatedSchema()` for
 * lists. ROUTE ORDER MATTERS — ts-rest registers routes onto Express in declaration order, first match
 * wins, so any `:param` route must be declared after the literal-path routes it could swallow. There is no
 * `GET /:param` here, but `apps/api/src/lib/contract-route-order.test.ts` enforces it for every contract.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { projectTaskSchema, taskOriginKindSchema } from './project.contract.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  instantSchema,
  legalEntityIdSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  taskPrioritySchema,
  taskStatusSchema,
  uuidSchema,
  workFormFieldTypeSchema,
  workFormKindSchema,
  workFormApprovalModeSchema,
  workFormModeSchema,
  workFormSystemFieldSchema,
  workPlanItemStatusSchema,
} from '../schemas/common.js';
import { TASK_PRIORITIES } from '../enums.js';

const c = initContract();

/**
 * Daily Work Planning Phase 3 — a non-negative `decimalStringSchema`, same local convention
 * `project.contract.ts`'s own `nonNegativeDecimalSchema` uses for the identical check on
 * `ProjectTask.achievedValue`.
 */
const nonNegativeDecimalSchema = decimalStringSchema.refine(
  (v) => Number.parseFloat(v) >= 0,
  'Must not be negative',
);

// ---------------------------------------------------------------------------
// Limits — shared by the schemas here and the route that validates submitted answers
// ---------------------------------------------------------------------------

export const WORK_FORM_LIMITS = {
  maxFields: 20,
  maxOptions: 20,
  labelMax: 80,
  optionMax: 60,
  textMax: 500,
  longTextMax: 2000,
  maxItemsPerSubmission: 20,
  maxCheckOutTasks: 50,
} as const;

// The response DTOs below are exported as `z.ZodType<Alias>` rather than as their inferred `ZodObject<...>`.
// Reason: the root `contract` (index.ts) lists every route of every module, and TypeScript refuses to
// serialise its inferred type once it grows past a fixed size (TS7056). A route that returns one of these
// DTOs would otherwise inline its whole object type at every use; annotated, it is printed once as a named
// alias. Runtime behaviour and the inferred request/response types are unchanged.

// ---------------------------------------------------------------------------
// DTOs — form definitions
// ---------------------------------------------------------------------------

/** What a field answer may be on the wire; the server checks it against the field's declared type. */
export const workFormRawValueSchema = z.union([
  z.string().max(WORK_FORM_LIMITS.longTextMax),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type WorkFormRawValue = z.infer<typeof workFormRawValueSchema>;

/** Which `type` each preset may have — a due date that is a checkbox cannot become a due date. */
const SYSTEM_FIELD_TYPES: Record<string, readonly string[]> = {
  priority: ['select'],
  due_date: ['date'],
  estimate_hours: ['number'],
  description: ['text', 'long_text'],
};

export const workFormFieldSchema = z
  .object({
    /** Stable identity of the field; answers are keyed by it. lower_snake, so it is safe as an object key. */
    key: z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,39}$/, 'Use lowercase letters, digits and underscores'),
    label: z.string().trim().min(1).max(WORK_FORM_LIMITS.labelMax),
    type: workFormFieldTypeSchema,
    required: z.boolean(),
    /** Only for `select`. */
    options: z
      .array(z.string().trim().min(1).max(WORK_FORM_LIMITS.optionMax))
      .max(WORK_FORM_LIMITS.maxOptions)
      .optional(),
    /** Present only on the four presets that map onto a task column. */
    system: workFormSystemFieldSchema.optional(),
    /** Pre-filled answer, as text (`'true'`/`'false'` for a checkbox). Employees can change it. */
    defaultValue: z.string().max(WORK_FORM_LIMITS.longTextMax).optional(),
  })
  .superRefine((field, ctx) => {
    const def = field.defaultValue;
    if (def !== undefined && def !== '') {
      let problem: string | null = null;
      if (field.type === 'number' && !Number.isFinite(Number(def))) {
        problem = 'The default must be a number';
      } else if (field.type === 'checkbox' && def !== 'true' && def !== 'false') {
        problem = 'The default must be checked or unchecked';
      } else if (field.type === 'select' && !(field.options ?? []).includes(def)) {
        problem = 'The default must be one of the options';
      } else if (field.type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(def)) {
        problem = 'The default must be a date';
      }
      if (problem) ctx.addIssue({ code: 'custom', path: ['defaultValue'], message: problem });
    }
    if (field.type === 'select') {
      if (!field.options || field.options.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['options'],
          message: 'A dropdown needs at least one option',
        });
      } else if (new Set(field.options).size !== field.options.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['options'],
          message: 'Dropdown options must be unique',
        });
      }
    } else if (field.options && field.options.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['options'], message: 'Only a dropdown has options' });
    }

    if (field.system) {
      const allowed = SYSTEM_FIELD_TYPES[field.system] ?? [];
      if (!allowed.includes(field.type)) {
        ctx.addIssue({
          code: 'custom',
          path: ['type'],
          message: `The ${field.system} field must be of type ${allowed.join(' or ')}`,
        });
      }
      // Priority maps onto the task's own priority column, so its options must be real priorities.
      if (
        field.system === 'priority' &&
        field.options?.some((o) => !(TASK_PRIORITIES as readonly string[]).includes(o))
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['options'],
          message: `Priority options must be from: ${TASK_PRIORITIES.join(', ')}`,
        });
      }
    }
  });
export type WorkFormField = z.infer<typeof workFormFieldSchema>;

/** Where the effective definition came from — lets the admin screen say "inherited from the org default". */
export const workFormSourceSchema = z.enum(['legal_entity', 'org_default', 'built_in']);

const workFormDefinitionSchemaShape = z.object({
  kind: workFormKindSchema,
  mode: workFormModeSchema,
  /** `null` = the org-wide default row. */
  legalEntityId: legalEntityIdSchema.nullable(),
  source: workFormSourceSchema,
  fields: z.array(workFormFieldSchema),
  /** Check-in form only (plan 4.6): does a submitted task need its manager's approval? Default `auto`. */
  approvalMode: workFormApprovalModeSchema,
  /** With `approvalMode = manager`: tasks on the Internal / Non-project project skip approval. */
  skipApprovalForInternal: z.boolean(),
  /** Manager-path tasks still awaiting a decision under this form, so HR is warned before switching modes. */
  pendingItemCount: z.number().int().nonnegative(),
  updatedAt: instantSchema.nullable(),
});
export type WorkFormDefinition = z.infer<typeof workFormDefinitionSchemaShape>;
export const workFormDefinitionSchema: z.ZodType<WorkFormDefinition> =
  workFormDefinitionSchemaShape;

const workFormDefinitionsSchemaShape = z.object({
  checkIn: workFormDefinitionSchema,
  checkOut: workFormDefinitionSchema,
});
export type WorkFormDefinitions = z.infer<typeof workFormDefinitionsSchemaShape>;
export const workFormDefinitionsSchema: z.ZodType<WorkFormDefinitions> =
  workFormDefinitionsSchemaShape;

// ---------------------------------------------------------------------------
// DTOs — check-in tasks
// ---------------------------------------------------------------------------

/** A submitted answer with its label and type snapshotted, so a later form edit never rewrites history. */
export const workFieldValueSchema = z.object({
  label: z.string(),
  type: workFormFieldTypeSchema,
  value: workFormRawValueSchema,
});
export type WorkFieldValue = z.infer<typeof workFieldValueSchema>;

const checkInItemSchemaShape = z.object({
  id: uuidSchema,
  planId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  date: dateOnlySchema,
  title: z.string(),
  projectId: uuidSchema.nullable(),
  projectName: z.string().nullable(),
  /** The preset fields, when HR's form includes them. */
  details: z.string().nullable(),
  priority: taskPrioritySchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  estimateHours: decimalStringSchema.nullable(),
  /** Every other HR-defined answer, keyed by field key. */
  values: z.record(z.string(), workFieldValueSchema),
  status: workPlanItemStatusSchema,
  /** The path this task took, fixed at submission: `auto` needed no approval (plan 4.6). */
  approvalMode: workFormApprovalModeSchema,
  rejectionReason: z.string().nullable(),
  /** Set once approved: the real project task now on the employee's To-Do list. */
  taskId: uuidSchema.nullable(),
  submittedAt: instantSchema.nullable(),
  decidedAt: instantSchema.nullable(),
});
export type CheckInItem = z.infer<typeof checkInItemSchemaShape>;
export const checkInItemSchema: z.ZodType<CheckInItem> = checkInItemSchemaShape;

const checkInDaySchemaShape = z.object({
  date: dateOnlySchema,
  /** The caller's own self-added check-in tasks — UNCHANGED by Daily Work Planning Phase 1
   * (`originKind: 'self_added'` once approved into a real task; this list itself is still every
   * `DailyWorkPlanItem` for the day, pending/approved/rejected, exactly as before). */
  items: z.array(checkInItemSchema),
  /**
   * Daily Work Planning Phase 1 (T1.5) — the caller's own `ProjectTask` rows assigned to them
   * (`originKind` of `manager_assigned` or `null`/legacy — NOT `carried_forward`, see
   * `carriedForwardTasks` below), due on this same day, still in an open (non-completed/cancelled)
   * status bucket. A separate section from `items` above: this is work a MANAGER assigned on the
   * real task board, not something the employee proposed at check-in.
   */
  assignedTasks: z.array(projectTaskSchema),
  /**
   * Daily Work Planning Phase 1 (T1.5) — the caller's own tasks with `originKind: 'carried_forward'`
   * due today. Always empty for now: nothing sets `carried_forward` until the Phase 7 carry-forward
   * action ships in a later batch. The field exists now so the response shape and the web page's
   * three sections (Assigned / Carried Forward / Self-Added) are stable ahead of that phase.
   */
  carriedForwardTasks: z.array(projectTaskSchema),
});
export type CheckInDay = z.infer<typeof checkInDaySchemaShape>;
export const checkInDaySchema: z.ZodType<CheckInDay> = checkInDaySchemaShape;

// ---------------------------------------------------------------------------
// DTOs — check-out report
// ---------------------------------------------------------------------------

export const checkOutTaskSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema.nullable(),
  taskTitle: z.string(),
  projectName: z.string().nullable(),
  statusAfter: taskStatusSchema,
  actualMinutes: z.number().int().nonnegative().nullable(),
  note: z.string().nullable(),
  /**
   * Daily Work Planning Phase 3 — the live task's measurable target/achieved/unit (Phase 2),
   * read for DISPLAY only; masked to null alongside `taskTitle`/`projectName` for the same
   * isolated-project leak `toCheckOut()` already guards (see workform.ts). `remainingValue` is
   * derived server-side (`targetValue - achievedValue`), same convention
   * `project.contract.ts`'s own doc comment on `ProjectTask.remainingValue` describes — never
   * stored, never sent as its own input.
   */
  targetValue: decimalStringSchema.nullable(),
  achievedValue: decimalStringSchema.nullable(),
  unit: z.string().nullable(),
  remainingValue: decimalStringSchema.nullable(),
  /** Daily Work Planning Phase 1 origin (never masked — not sensitive content, same as `statusAfter`). */
  originKind: taskOriginKindSchema.nullable(),
  /** For the dialog's own "planned vs actual" summary; same visibility rule as target/achieved. */
  estimateHours: decimalStringSchema.nullable(),
});
export type CheckOutTask = z.infer<typeof checkOutTaskSchema>;

const workCheckOutSchemaShape = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  date: dateOnlySchema,
  values: z.record(z.string(), workFieldValueSchema),
  note: z.string().nullable(),
  submittedAt: instantSchema,
  tasks: z.array(checkOutTaskSchema),
});
export type WorkCheckOut = z.infer<typeof workCheckOutSchemaShape>;
export const workCheckOutSchema: z.ZodType<WorkCheckOut> = workCheckOutSchemaShape;

/** A task the employee could report on at check-out: assigned to them and not yet closed. */
export const checkOutCandidateSchema = z.object({
  taskId: uuidSchema,
  title: z.string(),
  projectName: z.string(),
  status: taskStatusSchema,
  /** Daily Work Planning Phase 3 — same measurable target/achieved/unit/remaining as
   * `checkOutTaskSchema` above, for display before the task has even been reported on today. */
  targetValue: decimalStringSchema.nullable(),
  achievedValue: decimalStringSchema.nullable(),
  unit: z.string().nullable(),
  remainingValue: decimalStringSchema.nullable(),
  originKind: taskOriginKindSchema.nullable(),
  estimateHours: decimalStringSchema.nullable(),
});
export type CheckOutCandidate = z.infer<typeof checkOutCandidateSchema>;

const myCheckOutSchemaShape = z.object({
  date: dateOnlySchema,
  submission: workCheckOutSchema.nullable(),
  candidates: z.array(checkOutCandidateSchema),
});
export type MyCheckOut = z.infer<typeof myCheckOutSchemaShape>;
export const myCheckOutSchema: z.ZodType<MyCheckOut> = myCheckOutSchemaShape;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const getDefinitionsQuerySchema = z.object({
  /** Which legal entity's effective form to read. Omitted = the caller's own. */
  legalEntityId: legalEntityIdSchema.optional(),
  /** `true` = the org-wide default row itself (what the admin screen edits), not an effective lookup. */
  orgDefault: z.literal('true').optional(),
});

export const updateDefinitionParamsSchema = z.object({ kind: workFormKindSchema });

export const updateDefinitionBodySchema = z
  .object({
    /** Omitted = the org-wide default. */
    legalEntityId: legalEntityIdSchema.optional(),
    mode: workFormModeSchema,
    fields: z.array(workFormFieldSchema).max(WORK_FORM_LIMITS.maxFields),
    /** Check-in form only; omitted = keep what is stored (`auto` for a new form). Ignored on the check-out form. */
    approvalMode: workFormApprovalModeSchema.optional(),
    skipApprovalForInternal: z.boolean().optional(),
  })
  .superRefine((body, ctx) => {
    const keys = body.fields.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['fields'],
        message: 'Every field needs its own unique key',
      });
    }
    const systems = body.fields
      .map((f) => f.system)
      .filter((s): s is NonNullable<typeof s> => Boolean(s));
    if (new Set(systems).size !== systems.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['fields'],
        message: 'A preset field can only be used once',
      });
    }
  });

export const getMyCheckInQuerySchema = z.object({
  /** Omitted = today, resolved in the caller's location timezone (invariant 3). */
  date: dateOnlySchema.optional(),
});

const answersSchema = z.record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), workFormRawValueSchema);

export const submitCheckInItemSchema = z.object({
  title: z.string().trim().min(1).max(300),
  projectId: uuidSchema,
  /** Answers to HR's fields, keyed by field key. Validated against the CURRENT form definition. */
  values: answersSchema.default({}),
});

export const submitCheckInBodySchema = z.object({
  items: z.array(submitCheckInItemSchema).min(1).max(WORK_FORM_LIMITS.maxItemsPerSubmission),
});

export const resubmitCheckInItemBodySchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  projectId: uuidSchema.optional(),
  values: answersSchema.optional(),
});

export const listCheckInItemsQuerySchema = paginationQuerySchema.extend({
  /** `mine` = the caller's own; `team` = the people the caller may approve for. */
  scope: z.enum(['mine', 'team']).default('mine'),
  status: workPlanItemStatusSchema.optional(),
  personId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

export const decideCheckInItemBodySchema = z
  .object({
    action: z.enum(['approve', 'reject']),
    remarks: z.string().trim().max(500).optional(),
  })
  .refine((b) => b.action !== 'reject' || (b.remarks?.trim().length ?? 0) > 0, {
    message: 'A reason is required when rejecting a task',
    path: ['remarks'],
  });

export const decideBatchBodySchema = z
  .object({
    itemIds: z.array(uuidSchema).min(1).max(50),
    action: z.enum(['approve', 'reject']),
    remarks: z.string().trim().max(500).optional(),
  })
  .refine((b) => b.action !== 'reject' || (b.remarks?.trim().length ?? 0) > 0, {
    message: 'A reason is required when rejecting tasks',
    path: ['remarks'],
  });

export const decideBatchResponseSchema = z.object({
  results: z.array(
    z.object({
      itemId: uuidSchema,
      ok: z.boolean(),
      status: workPlanItemStatusSchema.optional(),
      error: z.string().optional(),
    }),
  ),
});

export const getMyCheckOutQuerySchema = z.object({
  date: dateOnlySchema.optional(),
});

export const submitCheckOutBodySchema = z.object({
  /** May be empty: a check-out with no task rows is valid, so a required form can never trap anyone. */
  tasks: z
    .array(
      z.object({
        taskId: uuidSchema,
        statusAfter: taskStatusSchema,
        actualMinutes: z.number().int().min(0).max(1440).optional(),
        note: z.string().trim().max(500).optional(),
        /**
         * Daily Work Planning Phase 3 — the employee's own reported progress against the task's
         * measurable target (Phase 2), reported from the check-out form itself rather than only
         * through `PATCH /projects/tasks/:id`. Same self-service rule as T2.3's carve-out: the
         * route accepts this only on the caller's OWN task (always true here — `submitCheckOut`
         * only ever loads tasks assigned to the caller), never `targetValue`/`unit`, which stay
         * manager/HR-set only. Omitted = leave the task's achievedValue exactly as it was.
         */
        achievedValue: nonNegativeDecimalSchema.optional(),
      }),
    )
    .max(WORK_FORM_LIMITS.maxCheckOutTasks)
    .default([]),
  values: answersSchema.default({}),
  note: z.string().trim().max(1000).optional(),
});

export const listCheckOutsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const workformContract = c.router(
  {
    // ---- Form definitions -------------------------------------------------
    getDefinitions: {
      method: 'GET',
      path: '/definitions',
      query: getDefinitionsQuerySchema,
      responses: { 200: workFormDefinitionsSchema, 401: errorBodySchema, 403: errorBodySchema },
      metadata: routeMeta(['work_plan:read', 'work_form:manage']),
      summary:
        'The effective check-in and check-out forms (entity row, else org default, else off)',
    },
    updateDefinition: {
      method: 'PUT',
      path: '/definitions/:kind',
      pathParams: updateDefinitionParamsSchema,
      body: updateDefinitionBodySchema,
      responses: {
        200: workFormDefinitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('work_form:manage'),
      summary:
        "Set a form's mode and fields for a legal entity or the org default — requires work_form:manage",
    },

    // ---- Check-in ---------------------------------------------------------
    getMyCheckIn: {
      method: 'GET',
      path: '/check-in/me',
      query: getMyCheckInQuerySchema,
      responses: { 200: checkInDaySchema, 401: errorBodySchema, 403: errorBodySchema },
      metadata: routeMeta('work_plan:read'),
      summary: "The caller's check-in tasks for a day, each with its approval state",
    },
    listCheckInItems: {
      method: 'GET',
      path: '/check-in/items',
      query: listCheckInItemsQuerySchema,
      responses: {
        200: paginatedSchema(checkInItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta(['work_plan:read', 'work_plan:approve']),
      summary: "Check-in tasks: the caller's own, or the team's for a manager's approval queue",
    },
    submitCheckInItems: {
      method: 'POST',
      path: '/check-in/items',
      body: submitCheckInBodySchema,
      responses: {
        201: checkInDaySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('work_plan:submit'),
      summary: 'Submit tasks at check-in; each one starts its own manager approval',
    },
    decideCheckInItemsBatch: {
      method: 'POST',
      path: '/check-in/items/decide-batch',
      body: decideBatchBodySchema,
      responses: {
        200: decideBatchResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('work_plan:approve'),
      summary: 'Approve or reject several tasks at once; each is still decided individually',
    },
    decideCheckInItem: {
      method: 'POST',
      path: '/check-in/items/:itemId/decide',
      body: decideCheckInItemBodySchema,
      responses: {
        200: checkInItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: "Approve (creates the task on the employee's To-Do list) or reject with a reason",
    },
    resubmitCheckInItem: {
      method: 'POST',
      path: '/check-in/items/:itemId/resubmit',
      body: resubmitCheckInItemBodySchema,
      responses: {
        200: checkInItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('work_plan:submit'),
      summary: 'Edit a rejected task and send it for approval again as a new submission',
    },
    deleteCheckInItem: {
      method: 'DELETE',
      path: '/check-in/items/:itemId',
      body: c.type<undefined>(),
      responses: {
        200: z.object({ success: z.boolean(), message: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('work_plan:submit'),
      summary: 'Discard a rejected task the employee will not resubmit',
    },

    // ---- Check-out --------------------------------------------------------
    getMyCheckOut: {
      method: 'GET',
      path: '/check-out/me',
      query: getMyCheckOutQuerySchema,
      responses: { 200: myCheckOutSchema, 401: errorBodySchema, 403: errorBodySchema },
      metadata: routeMeta('work_plan:read'),
      summary: "The caller's check-out report for a day, plus the tasks they could report on",
    },
    listCheckOuts: {
      method: 'GET',
      path: '/check-out',
      query: listCheckOutsQuerySchema,
      responses: {
        200: paginatedSchema(workCheckOutSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta('work_plan:read'),
      summary: "Check-out reports in the caller's visible scope (a manager sees their team's)",
    },
    submitCheckOut: {
      method: 'PUT',
      path: '/check-out/me',
      body: submitCheckOutBodySchema,
      responses: {
        200: workCheckOutSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('work_plan:submit'),
      summary: "Submit or replace today's check-out report; updates the reported tasks' statuses",
    },
  },
  { pathPrefix: '/work-forms' },
);
